var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useParams } from 'react-router-dom';
import { Slide, toast } from 'react-toastify';
import * as yup from 'yup';
import { ErrorMessageBox, FallbackComponent } from '../common';
import { TaskType } from '../hooks';
import { useThrottleEffect } from '../hooks/useThottleEffect';
import { PageBody } from '../layout/styled';
import { useReportContext } from '../report/context';
import { useReportOutletContext } from '../report/hooks';
import { ProgressToastContent } from '../report/progressToastContent';
import { useForbiddenRedirect, useReportAPI, useReportPlanType } from '../report/selectors';
import { useSimulatingInProgress, useToastMessagesAPI } from '../toastify/selector';
import { ALL_LOCATIONS_ID, ALL_QUEUES_ID, INIT_PERIOD_END, METRIC_LIST, NETWORK_ONLY_METRICS, SECTION_DATA } from './constants';
import { ControlPanel } from './controlPanel';
import { ControlPanelHeader } from './controlPanelHeader';
import { DataPanel } from './dataPanel';
import { ACTUAL_ATTRITION_MODE, DAILY_FILTER_KEYS, FREQUENCY_ENUM, METRICS_ENUM, SECTION_ENUM } from './enum';
import { ExportDialog } from './exportDialog';
import { createParamsFetchActual, useActualReportAPI, useActualStatus, useAsyncActualExport, useDeleteManualInputDataAPI, useQueueLocationsAPI, useQueuesAPI, useQueuesDropdown, useReportTimelineOptions, useScenarioDropdown, useSelectDisabledMetrics, useSelectFirstEnabledMetric, useUpdateActualDataManual } from './selectors';
import { useMetricSettingsAPI } from './settings/selectors';
// Validation schema
var validateForm = yup.object().shape({
  planId: yup.number().optional(),
  queueId: yup.number().optional(),
  metricTab: yup.string().required(),
  periodStart: yup.number().required(),
  periodEnd: yup.number().required(),
  frequency: yup.string().required(),
  dailyViewType: yup.string().required(),
  specificDay: yup.mixed().nullable(),
  actualAttritionMode: yup.string().nullable()
});
export var ActualMainPage = function () {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j;
  var allLocationsId = ALL_LOCATIONS_ID;
  var basePlanId = useParams().basePlanId;
  var _k = useAsyncActualExport(),
    exportActual = _k[0],
    fetchExportData = _k[1],
    clearExportData = _k[2];
  var _l = useToastMessagesAPI(),
    fetchToastMessagesData = _l[1];
  var context = useReportOutletContext();
  useEffect(function () {
    var _a, _b;
    (_a = context === null || context === void 0 ? void 0 : context.setIsLoadingExport) === null || _a === void 0 ? void 0 : _a.call(context, false);
    (_b = context === null || context === void 0 ? void 0 : context.setIsLoadingPrint) === null || _b === void 0 ? void 0 : _b.call(context, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var _m = useReportContext(),
    filterParams = _m.filterParams,
    setFilterParams = _m.setFilterParams;
  var actualParams = filterParams.actualParams;
  var initialValues = useMemo(function () {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    return {
      planId: actualParams === null || actualParams === void 0 ? void 0 : actualParams.planId,
      queueId: undefined,
      queueLocationId: (_a = actualParams === null || actualParams === void 0 ? void 0 : actualParams.queueLocationId) !== null && _a !== void 0 ? _a : allLocationsId,
      metricTab: (_b = actualParams === null || actualParams === void 0 ? void 0 : actualParams.metricTab) !== null && _b !== void 0 ? _b : METRICS_ENUM.WORK_OFFERED_HANDLED,
      periodStart: (_c = actualParams === null || actualParams === void 0 ? void 0 : actualParams.periodStart) !== null && _c !== void 0 ? _c : 0,
      periodEnd: (_d = actualParams === null || actualParams === void 0 ? void 0 : actualParams.periodEnd) !== null && _d !== void 0 ? _d : INIT_PERIOD_END,
      frequency: (_e = actualParams === null || actualParams === void 0 ? void 0 : actualParams.frequency) !== null && _e !== void 0 ? _e : FREQUENCY_ENUM.PERIOD,
      actualData: {},
      dailyViewType: (_f = actualParams === null || actualParams === void 0 ? void 0 : actualParams.dailyViewType) !== null && _f !== void 0 ? _f : DAILY_FILTER_KEYS.DAILY_AVERAGE,
      specificDay: (_g = actualParams === null || actualParams === void 0 ? void 0 : actualParams.specificDay) !== null && _g !== void 0 ? _g : null,
      actualAttritionMode: (_h = actualParams === null || actualParams === void 0 ? void 0 : actualParams.actualAttritionMode) !== null && _h !== void 0 ? _h : ACTUAL_ATTRITION_MODE.PERCENTAGE
    };
  }, [actualParams, allLocationsId]);
  // Form setup
  var methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validateForm)
  });
  var control = methods.control,
    setValue = methods.setValue,
    watch = methods.watch,
    handleSubmit = methods.handleSubmit;
  var _o = watch(),
    planId = _o.planId,
    frequency = _o.frequency,
    queueId = _o.queueId,
    queueLocationId = _o.queueLocationId,
    metricTab = _o.metricTab,
    periodStart = _o.periodStart,
    periodEnd = _o.periodEnd,
    dailyViewType = _o.dailyViewType,
    specificDay = _o.specificDay,
    actualAttritionMode = _o.actualAttritionMode;
  // Set default queue to be the first queue (not ALL_QUEUES)
  var _p = useState((_a = actualParams === null || actualParams === void 0 ? void 0 : actualParams.queueIndex) !== null && _a !== void 0 ? _a : 1),
    queueIndex = _p[0],
    setQueueIndex = _p[1];
  var _q = useState(false),
    dialogOpen = _q[0],
    setDialogOpen = _q[1];
  // APIs
  var _r = useReportAPI(),
    reportData = _r[0],
    fetchReport = _r[1];
  var _s = useQueuesAPI(planId),
    queues = _s[0],
    fetchQueues = _s[1];
  var _t = useQueueLocationsAPI(queueId),
    queueLocations = _t[0],
    fetchQueueLocations = _t[1],
    clearQueueLocations = _t[2];
  var locationOptions = useMemo(function () {
    var _a, _b;
    return ((_b = (_a = queueLocations.data) === null || _a === void 0 ? void 0 : _a.locations) !== null && _b !== void 0 ? _b : []).flatMap(function (location) {
      var _a, _b, _c;
      return location.queueLocationId == null ? [] : [{
        queueLocationId: location.queueLocationId,
        name: (_a = location.locationName) !== null && _a !== void 0 ? _a : "".concat((_b = location.region) !== null && _b !== void 0 ? _b : '', " ").concat((_c = location.country) !== null && _c !== void 0 ? _c : '').trim(),
        locationType: location.locationType,
        region: location.region,
        country: location.country
      }];
    });
  }, [queueLocations.data]);
  var _u = useState((_b = actualParams === null || actualParams === void 0 ? void 0 : actualParams.sectionType) !== null && _b !== void 0 ? _b : SECTION_ENUM.PERFORMANCE),
    sectionType = _u[0],
    setSectionType = _u[1];
  var _v = useMetricSettingsAPI(),
    metricSettings = _v[0],
    fetchMetricSettings = _v[1],
    clearMetricSettings = _v[2];
  var _w = useActualReportAPI(),
    reportActual = _w[0],
    fetchActualReport = _w[1],
    clearActualReport = _w[2];
  var _x = useActualStatus(),
    fetchActualStatus = _x[1],
    clearActualStatus = _x[2];
  var _y = useUpdateActualDataManual(),
    actualDataManualUpdated = _y[0],
    clearActualDataManualUpdated = _y[2];
  var _z = useDeleteManualInputDataAPI(),
    deleteManualInputData = _z[0],
    clearDeleteManualInputData = _z[2];
  useEffect(function () {
    var newParams = {
      planId: planId,
      sectionType: sectionType,
      queueIndex: queueIndex,
      queueLocationId: queueLocationId,
      metricTab: metricTab,
      periodStart: periodStart,
      periodEnd: periodEnd,
      frequency: frequency,
      dailyViewType: dailyViewType,
      specificDay: specificDay,
      actualAttritionMode: actualAttritionMode
    };
    setFilterParams(function (prev) {
      return __assign(__assign({}, prev), {
        actualParams: newParams
      });
    });
  }, [dailyViewType, frequency, metricTab, periodEnd, periodStart, planId, queueIndex, queueLocationId, sectionType, specificDay, actualAttritionMode, setFilterParams]);
  useEffect(function () {
    fetchReport();
  }, [fetchReport]);
  useEffect(function () {
    fetchQueues();
  }, [fetchQueues]);
  useThrottleEffect(function () {
    if (!queueId) return;
    fetchActualStatus({}, {
      planId: planId,
      sectionType: sectionType,
      queueId: queueId
    });
    return function () {
      clearActualStatus();
    };
  }, [fetchActualStatus, planId, sectionType, queueId, clearActualStatus]);
  useEffect(function () {
    fetchMetricSettings({}, {
      plan_id: basePlanId,
      section_type: sectionType
    });
    return function () {
      clearMetricSettings();
    };
  }, [fetchMetricSettings, basePlanId, sectionType, clearMetricSettings]);
  // Check Forbidden
  useForbiddenRedirect([reportActual.statusCode, metricSettings.statusCode, actualDataManualUpdated.statusCode, deleteManualInputData.statusCode, exportActual.statusCode]);
  // Get data
  var queuesMappingData = useQueuesDropdown(planId);
  var scenariosMappingData = useScenarioDropdown();
  var timelines = useReportTimelineOptions();
  var planType = useReportPlanType();
  var firstEnabledMetric = useSelectFirstEnabledMetric(sectionType);
  var metricDisabled = useSelectDisabledMetrics();
  useEffect(function () {
    fetchQueueLocations();
    return clearQueueLocations;
  }, [fetchQueueLocations, clearQueueLocations]);
  var selectedPlan = (_e = (_d = (_c = reportData.data) === null || _c === void 0 ? void 0 : _c.scenarios) === null || _d === void 0 ? void 0 : _d.find(function (scenario) {
    return scenario.id === planId;
  })) !== null && _e !== void 0 ? _e : reportData.data;
  var basePlan = (_h = (_g = (_f = reportData.data) === null || _f === void 0 ? void 0 : _f.scenarios) === null || _g === void 0 ? void 0 : _g.find(function (scenario) {
    return scenario.id === (selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.basePlanId);
  })) !== null && _h !== void 0 ? _h : reportData.data;
  var isNetworkOnlyMetric = NETWORK_ONLY_METRICS.includes(metricTab);
  var locationDisabled = isNetworkOnlyMetric || (basePlan === null || basePlan === void 0 ? void 0 : basePlan.importSource) === 'genesys' || queueId === ALL_QUEUES_ID;
  var hasSelectedLocation = queueLocationId !== undefined && queueLocationId !== allLocationsId;
  var handleExportSubmit = useCallback(function (data) {
    if (hasSelectedLocation) return;
    fetchExportData(data);
  }, [fetchExportData, hasSelectedLocation]);
  var isDisabled = function (id) {
    var _a;
    var queuesData = (_a = queues === null || queues === void 0 ? void 0 : queues.data) !== null && _a !== void 0 ? _a : [];
    if (!queuesData.length) return true;
    var disabledMap = Object.fromEntries(queuesData.map(function (q) {
      var _a;
      return [q.id, !((_a = q.status) === null || _a === void 0 ? void 0 : _a.wat)];
    }));
    var areAllQueuesEnabled = queuesData.every(function (q) {
      var _a;
      return (_a = q.status) === null || _a === void 0 ? void 0 : _a.wat;
    });
    disabledMap[ALL_QUEUES_ID] = !areAllQueuesEnabled || frequency === FREQUENCY_ENUM.DAILY;
    return !!disabledMap[id];
  };
  function handleFetchActualReport(forceNetworkScope) {
    if (forceNetworkScope === void 0) {
      forceNetworkScope = false;
    }
    handleSubmit(function (formData) {
      clearActualReport();
      fetchActualReport({}, createParamsFetchActual(forceNetworkScope ? __assign(__assign({}, formData), {
        queueLocationId: allLocationsId
      }) : formData));
    })();
  }
  useEffect(function () {
    if (locationDisabled || queueId === ALL_QUEUES_ID || queueId === undefined) return;
    handleFetchActualReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueLocationId]);
  useEffect(function () {
    if (queueId === undefined) return;
    handleFetchActualReport(locationDisabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricTab]);
  useEffect(function () {
    if (locationDisabled || queueLocations.requesting === false && !locationOptions.some(function (option) {
      return option.queueLocationId === queueLocationId;
    })) {
      setValue('queueLocationId', allLocationsId);
    }
  }, [allLocationsId, locationDisabled, locationOptions, queueLocationId, queueLocations.requesting, setValue]);
  useEffect(function () {
    var _a;
    if (!queuesMappingData.length) return;
    var safeIndex = Math.min(Math.max(queueIndex, 0), queuesMappingData.length - 1);
    var currentQueueId = (_a = queuesMappingData[safeIndex]) === null || _a === void 0 ? void 0 : _a.uniqueId;
    if (isDisabled(currentQueueId)) {
      var firstEnabledQueue = queuesMappingData.find(function (q) {
        return !isDisabled(q.uniqueId);
      });
      if (firstEnabledQueue) {
        setValue('queueId', firstEnabledQueue.uniqueId);
      }
    } else {
      setValue('queueId', currentQueueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuesMappingData, queueIndex, setValue]);
  useEffect(function () {
    if (timelines.length && periodEnd === INIT_PERIOD_END) {
      setValue('periodEnd', timelines.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelines.length, setValue]);
  useEffect(function () {
    if (!isEmpty(metricDisabled) && timelines.length) {
      if (metricDisabled[metricTab]) {
        setValue('metricTab', firstEnabledMetric);
      }
      handleFetchActualReport(locationDisabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricDisabled, setValue, timelines.length]);
  // Refetch actual data when manual update is successful
  useEffect(function () {
    if (actualDataManualUpdated.statusCode === HttpStatusCode.Ok) {
      handleFetchActualReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualDataManualUpdated.data]);
  var handleSectionTypeChange = function (type) {
    setSectionType(type);
    setValue('metricTab', METRIC_LIST[type][0]);
  };
  var handleOpenExportDialog = useCallback(function () {
    setDialogOpen(true);
  }, []);
  var handleCloseExportDialog = useCallback(function () {
    setDialogOpen(false);
  }, []);
  useEffect(function () {
    if (!context) return;
    context === null || context === void 0 ? void 0 : context.setOnExport(function () {
      return handleOpenExportDialog;
    });
    return function () {
      return context === null || context === void 0 ? void 0 : context.setOnExport(function () {
        return handleCloseExportDialog;
      });
    };
  }, [context, handleOpenExportDialog, handleCloseExportDialog]);
  var isSimulating = !!((_j = useSimulatingInProgress(basePlanId, TaskType.ActualReportExport)) === null || _j === void 0 ? void 0 : _j.length);
  useEffect(function () {
    var _a;
    (_a = context === null || context === void 0 ? void 0 : context.setIsDisabledExport) === null || _a === void 0 ? void 0 : _a.call(context, isSimulating || hasSelectedLocation);
    if (isSimulating) {
      var toastId_1 = toast.info(_jsx(ProgressToastContent, {
        taskType: TaskType.ActualReportExport
      }), {
        autoClose: false,
        position: 'bottom-right',
        theme: 'colored',
        closeButton: false,
        closeOnClick: false,
        transition: Slide,
        icon: false
      });
      return function () {
        return toast.dismiss(toastId_1);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelectedLocation, isSimulating]);
  useEffect(function () {
    if (exportActual === null || exportActual === void 0 ? void 0 : exportActual.data) {
      fetchToastMessagesData();
      return clearExportData();
    }
  }, [exportActual === null || exportActual === void 0 ? void 0 : exportActual.data, fetchToastMessagesData, clearExportData]);
  // Cleanup on unmount
  useEffect(function () {
    return function () {
      var _a;
      (_a = context === null || context === void 0 ? void 0 : context.setIsDisabledExport) === null || _a === void 0 ? void 0 : _a.call(context, false);
      clearActualDataManualUpdated();
      clearDeleteManualInputData();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return _jsxs(FallbackComponent, __assign({
    overlay: true,
    fixed: true,
    requesting: reportData.requesting || reportActual.requesting || metricSettings.requesting
  }, {
    children: [_jsx(ErrorMessageBox, {
      errors: [reportData.error, reportActual.error, metricSettings.error, actualDataManualUpdated.error, deleteManualInputData.error, exportActual.error]
    }), _jsx(Box, {
      children: _jsx(FormProvider, __assign({}, methods, {
        children: _jsxs(Box, __assign({
          component: "form"
        }, {
          children: [_jsx(ControlPanel, {
            filtersProps: {
              settingProps: {
                value: sectionType,
                options: SECTION_DATA,
                onChange: handleSectionTypeChange
              },
              selectorsProps: {
                control: control,
                scenarioOptions: scenariosMappingData,
                queueOptions: queuesMappingData,
                onQueueChange: function (value) {
                  return setQueueIndex(value);
                },
                locationOptions: locationOptions,
                locationDisabled: locationDisabled
              }
            }
          }), queueId && _jsx(DataPanel, {
            requesting: reportActual.requesting,
            planType: planType,
            sectionType: sectionType,
            timelines: timelines
          })]
        }))
      }))
    }), _jsx(ExportDialog, {
      open: dialogOpen,
      planType: planType,
      onClose: function () {
        return setDialogOpen(false);
      },
      onSubmit: handleExportSubmit
    })]
  }));
};
export var ActualLayout = function () {
  var context = useReportOutletContext();
  return _jsxs(PageBody, __assign({
    sx: {
      gap: 4
    },
    "data-testid": "page-body"
  }, {
    children: [_jsx(ControlPanelHeader, {}), _jsx(Outlet, {
      context: context
    })]
  }));
};